import { useEffect } from 'react';
import { t } from 'i18next';
import { ACCESS_MODEL, VideoProgressMinMax } from '@jwp/ott-common/src/constants';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useCheckoutStore } from '@jwp/ott-common/src/stores/CheckoutStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import type { AccessModel } from '@jwp/ott-common/types/config';
import type { EpgChannel } from '@jwp/ott-common/types/epg';
import type { MediaProduct } from '@jwp/ott-common/types/media';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { formatSeriesMetaString } from '@jwp/ott-common/src/utils/formatting';
import { isExpiredLiveEvent, isLiveEvent, isPlayableLiveEvent, MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import { isLiveChannel } from '@jwp/ott-common/src/utils/media';

import useEntitlement from './useEntitlement';

export type StartWatchingIconName = 'play' | 'scheduled';

export type StartWatchingAction = 'play' | 'authenticate' | 'choose-offer' | 'none';

export type StartWatchingTestId = 'start_watching' | 'buy' | 'sign_up' | 'complete_subscription' | 'no_access';

export type StartWatchingButtonState = {
  hasAccess: boolean;
  label: string;
  iconName: StartWatchingIconName | null;
  action: StartWatchingAction;
  progress: number | undefined;
  testId: StartWatchingTestId;
};

export type StartWatchingButtonInput = {
  item?: PlaylistItem;
  channel?: PlaylistItem | EpgChannel;
  isEntitled: boolean;
  isEntitlementLoading: boolean;
  mediaOffers: MediaProduct[];
  isLoggedIn: boolean;
  accessModel: AccessModel;
  videoProgress?: number;
  episodeMetadata?: { seasonNumber: string; episodeNumber: string };
  isSequentialEpisode?: boolean;
};

export const getStartWatchingButtonState = (input: StartWatchingButtonInput): StartWatchingButtonState => {
  const { item, channel, isEntitled, isEntitlementLoading, mediaOffers, isLoggedIn, accessModel, videoProgress, episodeMetadata, isSequentialEpisode } = input;

  const isEntitledEffective = isEntitlementLoading ? true : isEntitled;
  const hasMediaOffers = !!mediaOffers.length;

  const isPlayableItem = !!item && isPlayableLiveEvent(item);
  const channelCatchupHours = channel?.catchupHours ? Number(channel.catchupHours) : 0;
  const isExpiredChannelItem = !!item && !!channel && isExpiredLiveEvent(item, channelCatchupHours);
  const isLiveEventItem = isLiveEvent(item);
  const isLiveChannelItem = !!item && isLiveChannel(item);
  const isLiveItem = isLiveEventItem || isLiveChannelItem;

  const isAvodWithoutEntitlement = accessModel === ACCESS_MODEL.AVOD && !isEntitledEffective;

  const getOptimisticState = (): StartWatchingButtonState => ({
    hasAccess: true,
    label: t('video:start_watching'),
    iconName: isEntitledEffective ? 'play' : null,
    action: 'play',
    progress: videoProgress,
    testId: 'start_watching',
  });

  const getEntitledState = (): StartWatchingButtonState => {
    const isScheduledLiveEvent = isLiveEventItem && !isPlayableItem;
    const isExpiredLiveItem = isLiveItem && isExpiredChannelItem;
    const isUnplayableLiveItem = isScheduledLiveEvent || isExpiredLiveItem;
    const isCurrentlyLive = isLiveItem && item?.mediaStatus === MediaStatus.LIVE;
    const hasVideoProgress = typeof videoProgress === 'number';

    const getLabel = (): string => {
      const { seasonNumber, episodeNumber } = episodeMetadata || {};
      const seriesMetaString = formatSeriesMetaString(seasonNumber, episodeNumber);
      const seriesSuffix = seriesMetaString ? ` ${seriesMetaString}` : '';

      if (isExpiredLiveItem) return t('video:is_expired');
      if (isLiveChannelItem || isCurrentlyLive) return t('video:watch_live');
      if (isScheduledLiveEvent) return t('common:scheduled');
      if (hasVideoProgress) return `${t('video:continue_watching')}${seriesSuffix}`;
      if (isSequentialEpisode) return `${t('video:start_watching')}${seriesSuffix}`;

      return t('video:start_watching');
    };

    const getIconName = (): StartWatchingIconName | null => {
      if (isLiveChannelItem) return 'play';
      if (isScheduledLiveEvent) return 'scheduled';
      if (isPlayableItem) return 'play';
      return null;
    };

    return {
      hasAccess: true,
      label: getLabel(),
      iconName: getIconName(),
      action: isUnplayableLiveItem ? 'none' : 'play',
      progress: videoProgress,
      testId: 'start_watching',
    };
  };

  const getNotEntitledState = (): StartWatchingButtonState => {
    let label: string;
    let testId: StartWatchingTestId;

    if (hasMediaOffers) {
      label = t('video:buy');
      testId = 'buy';
    } else if (!isLoggedIn) {
      label = t('video:sign_up_to_start_watching');
      testId = 'sign_up';
    } else {
      label = t('video:complete_your_subscription');
      testId = 'complete_subscription';
    }

    return {
      hasAccess: true,
      label,
      iconName: null,
      action: isLoggedIn ? 'choose-offer' : 'authenticate',
      progress: videoProgress,
      testId,
    };
  };

  if (!item) {
    return getOptimisticState();
  }

  if (isAvodWithoutEntitlement) {
    return {
      hasAccess: false,
      label: t('video:no_access'),
      iconName: null,
      action: 'none',
      progress: videoProgress,
      testId: 'no_access',
    };
  }

  if (isEntitledEffective) {
    return getEntitledState();
  }

  return getNotEntitledState();
};

type Props = {
  item?: PlaylistItem;
  channel?: PlaylistItem | EpgChannel;
  episodeMetadata?: { seasonNumber: string; episodeNumber: string };
  isSequentialEpisode?: boolean;
};

const useStartWatchingButton = ({ item, channel, episodeMetadata, isSequentialEpisode }: Props) => {
  const accessModel = useConfigStore((state) => state.accessModel);
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const { isEntitled, isLoading: isEntitlementLoading, mediaOffers } = useEntitlement(item);
  const watchHistoryItem = useWatchHistoryStore((state) => (item ? state.getItem(item, !isSequentialEpisode) : undefined));
  const videoProgress = watchHistoryItem?.progress && watchHistoryItem?.progress > VideoProgressMinMax.Min ? watchHistoryItem?.progress : undefined;

  const setRequestedMediaProducts = useCheckoutStore((s) => s.setRequestedMediaProducts);

  useEffect(() => {
    // set the TVOD mediaOffers in the checkout store
    setRequestedMediaProducts(mediaOffers);

    return () => setRequestedMediaProducts([]);
  }, [mediaOffers, setRequestedMediaProducts]);

  return getStartWatchingButtonState({
    item,
    channel,
    isEntitled,
    isEntitlementLoading,
    mediaOffers,
    isLoggedIn,
    accessModel,
    videoProgress,
    episodeMetadata,
    isSequentialEpisode,
  });
};

export default useStartWatchingButton;
